<template>
    <v-sheet
        color="primary"
        elevation="8"
        rounded="sm"
        class="floor-area-panel pa-3"
        @click.stop
        @mousedown.stop
        @touchstart.stop
    >
        <div class="floor-area-panel__dimension-row d-flex mb-2">
            <v-text-field
                :model-value="lengthCm"
                type="number"
                :label="$t('formFields.lengthCm')"
                density="compact"
                hide-details="auto"
                variant="outlined"
                color="yellow"
                base-color="yellow"
                class="floor-area-panel__number-input"
                @update:model-value="$emit('update:lengthCm', $event)"
                @keydown.enter="onEnter"
            />
            <v-text-field
                :model-value="widthCm"
                type="number"
                :label="$t('formFields.widthCm')"
                density="compact"
                hide-details="auto"
                variant="outlined"
                color="yellow"
                base-color="yellow"
                class="floor-area-panel__number-input"
                @update:model-value="$emit('update:widthCm', $event)"
                @keydown.enter="onEnter"
            />
        </div>

        <v-text-field
            :model-value="packageCount"
            type="number"
            :label="$t('formFields.packageCount')"
            density="compact"
            hide-details="auto"
            variant="outlined"
            color="yellow"
            base-color="yellow"
            class="floor-area-panel__number-input mb-2"
            @update:model-value="$emit('update:packageCount', $event)"
            @keydown.enter="onEnter"
        />

        <div class="floor-area-panel__result-row d-flex align-start">
            <span class="text-body-1 text-white font-weight-bold flex-shrink-0 me-2 pt-2">
                {{ $t("formFields.floorAreaResult") }}:
            </span>
            <div class="floor-area-panel__result-content d-flex align-start flex-grow-1 ga-1">
                <v-text-field
                    :model-value="resultDisplay"
                    readonly
                    density="compact"
                    hide-details="auto"
                    variant="outlined"
                    color="yellow"
                    base-color="yellow"
                    class="floor-area-panel__number-input floor-area-panel__result-input"
                />
                <span class="d-inline-flex flex-shrink-0 pt-1">
                    <v-btn
                        icon
                        size="small"
                        variant="text"
                        color="yellow"
                        class="floor-area-panel__copy-btn"
                        :disabled="!copyableResultValue"
                        data-cy="floor-area-copy-button"
                        @mousedown.stop.prevent
                        @click.stop.prevent="copyResult"
                    >
                        <v-icon size="18">mdi-content-copy</v-icon>
                    </v-btn>
                    <v-tooltip activator="parent" location="bottom">
                        {{ $t("entries.floorAreaCopyResult") }}
                    </v-tooltip>
                </span>
            </div>
        </div>
    </v-sheet>
</template>

<script>
import { showAlertModal } from "@/utils";
import { formatFloorAreaCopyValue } from "@/utils/floorAreaCalculation";

export default {
    name: "FloorAreaPanel",
    props: {
        lengthCm: {
            type: [String, Number],
            default: "",
        },
        widthCm: {
            type: [String, Number],
            default: "",
        },
        packageCount: {
            type: [String, Number],
            default: "1",
        },
    },
    emits: ["update:lengthCm", "update:widthCm", "update:packageCount", "apply"],
    data() {
        return {
            suppressCloseApply: false,
        };
    },
    computed: {
        resultDisplay() {
            const value = formatFloorAreaCopyValue(
                this.lengthCm,
                this.widthCm,
                this.packageCount
            );
            if (!value) return "";
            return `${value} ${this.$t("entries.floorAreaUnit")}`;
        },
        copyableResultValue() {
            return formatFloorAreaCopyValue(
                this.lengthCm,
                this.widthCm,
                this.packageCount
            );
        },
    },
    methods: {
        shouldSuppressCloseApply() {
            return this.suppressCloseApply;
        },
        onEnter(event) {
            event.preventDefault();
            this.tryApply();
        },
        async copyResult() {
            if (!this.copyableResultValue) return;

            this.suppressCloseApply = true;

            try {
                await navigator.clipboard.writeText(this.copyableResultValue);
                showAlertModal(
                    this.$store,
                    this.$t("entries.floorAreaResultCopied", {
                        value: this.copyableResultValue,
                    }),
                    "success",
                    1500
                );
            } catch (error) {
                console.error("Failed to copy floor area result:", error);
            } finally {
                window.setTimeout(() => {
                    this.suppressCloseApply = false;
                }, 200);
            }
        },
        tryApply() {
            const resultLabel = formatFloorAreaCopyValue(
                this.lengthCm,
                this.widthCm,
                this.packageCount
            );
            if (!resultLabel) return;

            this.$emit("apply", {
                lengthCm: this.lengthCm,
                widthCm: this.widthCm,
                packageCount: this.packageCount,
                resultLabel,
            });
        },
    },
};
</script>

<style scoped>
.floor-area-panel {
    width: 320px;
    max-width: min(320px, calc(100vw - 24px));
    border: 1px solid rgba(255, 235, 59, 0.35);
}

.floor-area-panel__dimension-row {
    gap: 12px;
}

.floor-area-panel__number-input {
    flex: 1 1 auto;
    min-width: 0;
}

.floor-area-panel__result-row {
    margin-top: 4px;
}

.floor-area-panel__result-content {
    min-width: 0;
}

.floor-area-panel__result-input {
    flex: 1 1 auto;
}

.floor-area-panel__copy-btn {
    opacity: 0.95;
}

.floor-area-panel__copy-btn:disabled {
    opacity: 0.35;
}

.floor-area-panel__number-input :deep(input[type="number"]::-webkit-inner-spin-button),
.floor-area-panel__number-input :deep(input[type="number"]::-webkit-outer-spin-button) {
    -webkit-appearance: none;
    margin: 0;
}

.floor-area-panel__number-input :deep(input[type="number"]) {
    -moz-appearance: textfield;
    appearance: textfield;
}
</style>
